import React, { useEffect, useState } from 'react';
import {
  Alert, Button, Stack,
} from '@mui/material';
import unifiedApi from '../../utils/unifiedApi';
import {
  enablePushOnDevice, getCurrentSubscription, getNotificationPermission, isPushSupported, needsInstallForPush,
} from '../../utils/pushNotifications';
import { IosInstallHelp } from './ChatNotificationSettingsDialog';

export const PROMPT_DISMISSED_KEY = 'chatPushPromptDismissed';

const isDismissed = () => {
  try {
    return localStorage.getItem(PROMPT_DISMISSED_KEY) === '1';
  } catch {
    return false;
  }
};

/** One-time invitation in the chat list to turn on notifications for this device. */
const ChatNotificationPrompt = ({ onOpenSettings }) => {
  const [state, setState] = useState({ visible: false, mode: null, vapidPublicKey: null });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (isDismissed()) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const config = await unifiedApi.getChatPushConfig();
        if (cancelled || !config?.enabled) return;
        if (needsInstallForPush()) {
          setState({ visible: true, mode: 'install', vapidPublicKey: config.vapidPublicKey });
          return;
        }
        if (!isPushSupported() || getNotificationPermission() !== 'default') return;
        const subscription = await getCurrentSubscription().catch(() => null);
        if (!cancelled && !subscription) setState({ visible: true, mode: 'enable', vapidPublicKey: config.vapidPublicKey });
      } catch {
        // Prompt is optional.
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const dismiss = () => {
    try {
      localStorage.setItem(PROMPT_DISMISSED_KEY, '1');
    } catch {
      // ignore
    }
    setState((s) => ({ ...s, visible: false }));
  };

  const enable = async () => {
    setBusy(true);
    try {
      await enablePushOnDevice(state.vapidPublicKey);
    } catch {
      onOpenSettings();
    } finally {
      setBusy(false);
      dismiss();
    }
  };

  if (!state.visible) return null;

  if (state.mode === 'install') {
    return (
      <Stack spacing={1} sx={{ p: 1.5 }}>
        <IosInstallHelp />
        <Button size="small" onClick={dismiss} sx={{ alignSelf: 'flex-end' }}>Not now</Button>
      </Stack>
    );
  }

  return (
    <Alert
      severity="info"
      sx={{ m: 1.5 }}
      action={(
        <Stack direction="row" spacing={1}>
          <Button size="small" color="inherit" onClick={dismiss}>Not now</Button>
          <Button size="small" variant="contained" onClick={enable} disabled={busy}>Turn on</Button>
        </Stack>
      )}
    >
      Get notified about new messages?
    </Alert>
  );
};

export default ChatNotificationPrompt;
